import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  OAuthProvider,
  User,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth';
import { get, ref } from 'firebase/database';
import { auth, database } from '../services/firebase';
import { ensureUserProfile } from '../services/authProviders';
import logo from '../logo.png';
import appleLogo from '../apple-logo.png';
import googleLogo from '../google-logo.png';
import './SignIn.css';

type LocationState = {
  registrationEmail?: string;
  accountCreated?: boolean;
  returnTo?: string;
};

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const locationState = location.state as LocationState | null;
  const returnTo = locationState?.returnTo;
  const isBusy = loading || Boolean(socialLoading);

  useEffect(() => {
    if (!locationState) {
      return;
    }

    if (locationState.registrationEmail) {
      setEmail(locationState.registrationEmail);
    }

    if (locationState.accountCreated) {
      setNotice('Account created! You can sign in now.');
    }
  }, [locationState]);

  useEffect(() => {
    setError(null);
  }, [email, password]);

  const mapFirebaseError = useCallback((code: string) => {
    switch (code) {
      case 'auth/invalid-email':
        return 'Enter a valid email address.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Incorrect email or password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Try again later.';
      default:
        return 'Unable to sign in. Please try again.';
    }
  }, []);

  const mapSocialError = useCallback((code: string) => {
    switch (code) {
      case 'auth/popup-blocked':
        return 'Enable pop-ups to continue with this provider.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled before completing.';
      case 'auth/cancelled-popup-request':
        return 'Another sign-in is in progress. Please try again.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email. Try a different sign-in method.';
      default:
        return 'Unable to complete social sign-in. Please try again.';
    }
  }, []);

  const completeSignIn = useCallback(
    async (user: User) => {
      if (!user) {
        return;
      }

      try {
        const profileSnapshot = await get(ref(database, `users/${user.uid}`));
        const profile = profileSnapshot.val();

        if (profile?.needsOrganizationSetup) {
          navigate('/organization/create', {
            replace: true,
            state: returnTo ? { returnTo } : undefined,
          });
          return;
        }
      } catch (profileError) {
        console.warn('Failed to load user profile for onboarding check', profileError);
      }

      navigate(returnTo ?? '/dashboard', { replace: true });
    },
    [navigate, returnTo]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!email.trim() || !password) {
        setError('Email and password are required.');
        return;
      }

      setLoading(true);
      setError(null);
      setNotice(null);

      try {
        const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
        const user = credential.user;

        if (user) {
          await completeSignIn(user);
        }
      } catch (err) {
        const code =
          typeof err === 'object' && err && 'code' in err ? String((err as { code?: unknown }).code) : '';
        setError(mapFirebaseError(code));
      } finally {
        setLoading(false);
      }
    },
    [email, password, completeSignIn, mapFirebaseError]
  );

  const handleGoogleSignIn = useCallback(async () => {
    setError(null);
    setNotice(null);
    setSocialLoading('google');

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      const credential = await signInWithPopup(auth, provider);
      const user = credential.user;
      const profile = getAdditionalUserInfo(credential)?.profile as
        | { given_name?: string; family_name?: string; picture?: string; email?: string }
        | undefined;

      if (user) {
        await ensureUserProfile(user, {
          email: profile?.email ?? user.email ?? null,
          firstName: profile?.given_name ?? null,
          lastName: profile?.family_name ?? null,
          displayName: user.displayName ?? null,
          photoURL: (profile?.picture as string | undefined) ?? user.photoURL ?? null,
        });

        await completeSignIn(user);
      }
    } catch (err) {
      console.warn('Google sign-in failed', err);
      const code =
        typeof err === 'object' && err && 'code' in err ? String((err as { code?: unknown }).code) : '';
      setError(mapSocialError(code));
    } finally {
      setSocialLoading(null);
    }
  }, [completeSignIn, mapSocialError]);

  const handleAppleSignIn = useCallback(async () => {
    setError(null);
    setNotice(null);
    setSocialLoading('apple');

    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const credential = await signInWithPopup(auth, provider);
      const user = credential.user;
      const profile = getAdditionalUserInfo(credential)?.profile as
        | { email?: string; name?: { firstName?: string; lastName?: string } }
        | undefined;

      if (user) {
        await ensureUserProfile(user, {
          email: profile?.email ?? user.email ?? null,
          firstName: profile?.name?.firstName ?? null,
          lastName: profile?.name?.lastName ?? null,
          displayName: user.displayName ?? null,
          photoURL: user.photoURL ?? null,
        });

        await completeSignIn(user);
      }
    } catch (err) {
      console.warn('Apple sign-in failed', err);
      const code =
        typeof err === 'object' && err && 'code' in err ? String((err as { code?: unknown }).code) : '';
      setError(mapSocialError(code));
    } finally {
      setSocialLoading(null);
    }
  }, [completeSignIn, mapSocialError]);

  return (
    <div className="signin-root">
      <div className="signin-card">
        <div className="signin-header">
          <img src={logo} alt="Happy Tail Tracker logo" className="signin-logo" />
          <h1 className="signin-title">Happy Tail Tracker</h1>
        </div>
        <form className="signin-form" onSubmit={handleSubmit}>
          <input
            className="signin-input"
            id="email"
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={isBusy}
          />

          <input
            className="signin-input"
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={isBusy}
          />

          {notice ? (
            <p className="signin-notice" role="status">
              {notice}
            </p>
          ) : null}
          {error ? (
            <p className="signin-error" role="alert">
              {error}
            </p>
          ) : null}

          <button className="signin-button" type="submit" disabled={isBusy}>
            {loading ? 'Signing you in...' : 'Sign In'}
          </button>
        </form>

        <div className="signin-divider" aria-hidden="true">
          <span className="signin-divider-line" />
          <span className="signin-divider-text">Or</span>
          <span className="signin-divider-line" />
        </div>

        <div className="signin-social">
          <button
            type="button"
            className="signin-social-button signin-social-google"
            disabled={isBusy}
            onClick={handleGoogleSignIn}
          >
            {socialLoading === 'google' ? (
              'Signing in with Google…'
            ) : (
              <>
                <img src={googleLogo} alt="" className="signin-social-logo" />
                <span>Continue with Google</span>
              </>
            )}
          </button>
          <button
            type="button"
            className="signin-social-button signin-social-apple"
            disabled={isBusy}
            onClick={handleAppleSignIn}
          >
            {socialLoading === 'apple' ? (
              'Signing in with Apple…'
            ) : (
              <>
                <img src={appleLogo} alt="" className="signin-social-logo" />
                <span>Continue with Apple</span>
              </>
            )}
          </button>
        </div>

        <button
          type="button"
          className="signin-secondary-action"
          disabled={isBusy}
          onClick={() =>
            navigate('/register', {
              state: returnTo ? { returnTo } : undefined,
            })}
        >
          Need an account? Sign up
        </button>
      </div>
    </div>
  );
}
